import React, { useEffect, useMemo, useState } from "react";
import StyledModal from "../../components/CustomModal";
import { FooterButtons } from "./styles";
import { Form, FormInstance, Input, Select, InputNumber, Row, Col } from "antd";
import { useAppSelector } from "../../utils/hooks/redux";
import { FormWrapper, StyledButton } from "../../components/styles";
import { selectPricingPlan } from "../../redux/features/pricingPlan/pricingPlanTypeSlice";
import { selectFeatures } from "../../redux/features/feature/featuresTypeSlice";

const languageOptions = [
    { code: "en", label: "English" },
    { code: "es", label: "Spanish" },
    { code: "fr", label: "French" },
    { code: "de", label: "German" },
    { code: "pt", label: "Portuguese" },
    { code: "ar", label: "Arabic" },
];

const VALUE_TYPE_OPTIONS = [
    { label: "— Not Included", value: "not_included" },
    { label: "✅ Included", value: "check" },
    { label: "Custom Text", value: "custom" },
];

type FeatureValueEntry = {
    type: "not_included" | "check" | "custom";
    customText: string;
};

type ModalProps = {
    isModalOpen: boolean;
    handleSave: (v: any) => void;
    onCancel: () => void;
    form: FormInstance<any>;
    hasAddPermission: boolean;
    hasEditPermission: boolean;
    hasViewPermission: boolean;
};

const CreateEditModal: React.FC<ModalProps> = ({ isModalOpen, handleSave, onCancel, form, hasAddPermission, hasEditPermission, hasViewPermission }) => {
    const { loading } = useAppSelector(selectPricingPlan);
    const { data: features } = useAppSelector(selectFeatures);
    const canSaveChanges = useMemo(() => hasAddPermission || hasEditPermission, [hasAddPermission, hasEditPermission]);

    // Local state for per-feature values
    const [featureEntries, setFeatureEntries] = useState<Record<string, FeatureValueEntry>>({});

    const parseTranslations = (value: any) => {
        if (!value) return {};
        if (typeof value === "string") {
            try {
                return JSON.parse(value) || {};
            } catch (e) {
                return {};
            }
        }
        return value;
    };

    // Initialize feature entries from form data when modal opens
    useEffect(() => {
        if (isModalOpen) {
            const record = form.getFieldsValue(true);

            form.setFieldsValue({
                ...record,
                amount: record.amount ? Number(record.amount) : undefined,
                days: record.days ? Number(record.days) : undefined,
                translationKey: record.translationKey || undefined,
                titleTranslations: parseTranslations(record.titleTranslations),
                descriptionTranslations: parseTranslations(record.descriptionTranslations),
                emailTemplates: record.emailTemplates || {},
            });

            // Build featureEntries from existing featureValues or features array
            const entries: Record<string, FeatureValueEntry> = {};
            const existingValues = record?.featureValues || {};

            if (Object.keys(existingValues).length > 0) {
                // Use featureValues (new format)
                for (const [featureId, value] of Object.entries(existingValues)) {
                    if (value === "check") {
                        entries[featureId] = { type: "check", customText: "" };
                    } else {
                        entries[featureId] = { type: "custom", customText: value as string };
                    }
                }
            } else if (record?.features?.length) {
                // Backward compat: convert old features array to entries
                const featureIds = record.features.map((f: any) => {
                    if (typeof f === "string") return f;
                    if (typeof f === "object" && f?.id) return f.id;
                    return undefined;
                }).filter(Boolean);
                for (const fId of featureIds) {
                    entries[fId] = { type: "check", customText: "" };
                }
            }

            setFeatureEntries(entries);
        } else {
            setFeatureEntries({});
        }
    }, [isModalOpen, form, features]);

    const handleFeatureTypeChange = (featureId: string, type: string) => {
        setFeatureEntries((prev) => ({
            ...prev,
            [featureId]: {
                type: type as FeatureValueEntry["type"],
                customText: type === "custom" ? (prev[featureId]?.customText || "") : "",
            },
        }));
    };

    const handleFeatureCustomTextChange = (featureId: string, text: string) => {
        setFeatureEntries((prev) => ({
            ...prev,
            [featureId]: { ...prev[featureId], customText: text },
        }));
    };

    const buildFeatureValues = (): Record<string, string> => {
        const values: Record<string, string> = {};
        for (const [featureId, entry] of Object.entries(featureEntries)) {
            if (entry.type === "check") {
                values[featureId] = "check";
            } else if (entry.type === "custom" && entry.customText.trim()) {
                values[featureId] = entry.customText.trim();
            }
            // "not_included" → omit from values
        }
        return values;
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const featureValues = buildFeatureValues();
            const features = Object.keys(featureValues);
            // Clean empty email template entries
            const emailTemplates: Record<string, string> = {};
            if (values.emailTemplates) {
                for (const [key, value] of Object.entries(values.emailTemplates)) {
                    if (value && (value as string).trim()) {
                        emailTemplates[key] = (value as string).trim();
                    }
                }
            }
            handleSave({ ...values, featureValues, features, emailTemplates });
        } catch (error) {
            console.error("Validation failed:", error);
        }
    };

    const sortedFeatures = useMemo(() => {
        return [...(features || [])].sort((a, b) => (a.index ?? 999) - (b.index ?? 999));
    }, [features]);

    return (
        <StyledModal
            draggable={true}
            width={700}
            title={form.getFieldValue('id') ? `${canSaveChanges ? "Edit" : "View"} Pricing Plan` : `${canSaveChanges ? "Add" : "View"} Pricing Plan`}
            open={isModalOpen}
            closeIcon={null}
            footer={
                <FooterButtons>
                    <StyledButton onClick={onCancel} disabled={loading}>
                        Cancel
                    </StyledButton>
                    {canSaveChanges && <StyledButton
                        type="primary"
                        onClick={handleSubmit}
                        loading={loading}
                    >
                        Save
                    </StyledButton>}
                </FooterButtons>
            }
            centered
            onCancel={() => !loading && onCancel()}
        >
            <FormWrapper form={form} layout="vertical">
                <Form.Item label="Pricing Plan Title" name="title" rules={[{ required: true, message: "Please enter Pricing Plan Title" }]}>
                    <Input size="large" disabled={loading || !canSaveChanges} />
                </Form.Item>
                <Form.Item label="Description" name="description" rules={[{ required: true, message: "Please enter Description" }]}>
                    <Input size="large" disabled={loading || !canSaveChanges} />
                </Form.Item>
                <Row gutter={12}>
                    <Col span={6}>
                        <Form.Item name="amount" label="Amount ($ USD)" rules={[{ required: true, message: "Please enter Amount" }]}>
                            <InputNumber size="large" style={{ width: "100%" }} min={0} disabled={loading || !canSaveChanges} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name={["currencyPrices", "EUR"]} label="Amount (€ EUR)">
                            <InputNumber size="large" style={{ width: "100%" }} min={0} disabled={loading || !canSaveChanges} placeholder="e.g. 13" />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name={["currencyPrices", "GBP"]} label="Amount (£ GBP)">
                            <InputNumber size="large" style={{ width: "100%" }} min={0} disabled={loading || !canSaveChanges} placeholder="e.g. 11" />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="days" label="Days" rules={[{ required: true, message: "Please enter Days" }]}>
                            <InputNumber size="large" style={{ width: "100%" }} min={1} disabled={loading || !canSaveChanges} />
                        </Form.Item>
                    </Col>
                </Row>

                <div style={{ marginBottom: 12, fontWeight: 600, fontSize: 14 }}>Feature Values</div>
                <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden", marginBottom: 16 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 160px 1fr", gap: 0, background: "#f9fafb", padding: "8px 12px", borderBottom: "1px solid #e5e7eb", fontWeight: 600, fontSize: 12, color: "#6b7280" }}>
                        <span>Feature</span>
                        <span>Value Type</span>
                        <span>Custom Value</span>
                    </div>
                    {sortedFeatures.map((feature) => {
                        const entry = featureEntries[feature.id] || { type: "not_included", customText: "" };
                        return (
                            <div key={feature.id} style={{ display: "grid", gridTemplateColumns: "1fr 160px 1fr", gap: 8, padding: "8px 12px", borderBottom: "1px solid #f0f0f0", alignItems: "center" }}>
                                <span style={{ fontSize: 13, fontWeight: 500 }}>{feature.name}</span>
                                <Select
                                    size="small"
                                    value={entry.type}
                                    onChange={(val) => handleFeatureTypeChange(feature.id, val)}
                                    options={VALUE_TYPE_OPTIONS}
                                    disabled={loading || !canSaveChanges}
                                    style={{ width: "100%" }}
                                />
                                {entry.type === "custom" ? (
                                    <Input
                                        size="small"
                                        value={entry.customText}
                                        onChange={(e) => handleFeatureCustomTextChange(feature.id, e.target.value)}
                                        placeholder="Enter display text"
                                        disabled={loading || !canSaveChanges}
                                    />
                                ) : (
                                    <span style={{ color: "#d1d5db", fontSize: 12 }}>—</span>
                                )}
                            </div>
                        );
                    })}
                    {sortedFeatures.length === 0 && (
                        <div style={{ padding: 16, textAlign: "center", color: "#9ca3af", fontSize: 13 }}>
                            No features created yet. Add features first.
                        </div>
                    )}
                </div>

                <Form.Item label="Translation Key" name="translationKey" tooltip="Optional i18n key used on the public site">
                    <Input size="large" disabled={loading || !canSaveChanges} placeholder="e.g. pricing.plan.standard" />
                </Form.Item>

                <div style={{ marginBottom: 8, fontWeight: 600 }}>Localized Titles</div>
                <Row gutter={[12, 12]}>
                    {languageOptions.map((lang) => (
                        <Col span={12} key={lang.code}>
                            <Form.Item name={["titleTranslations", lang.code]} label={lang.label}>
                                <Input size="large" disabled={loading || !canSaveChanges} placeholder={`Enter ${lang.label} title`} />
                            </Form.Item>
                        </Col>
                    ))}
                </Row>

                <div style={{ marginBottom: 8, fontWeight: 600 }}>Email Templates (SendGrid IDs)</div>
                <div style={{ marginBottom: 8, fontSize: 12, color: '#6b7280' }}>
                    Leave blank to use the default template. Only set if this plan has a custom SendGrid template.
                </div>
                <Row gutter={[12, 12]}>
                    <Col span={24}>
                        <Form.Item
                            name={["emailTemplates", "PaymentSuccess"]}
                            label="Payment Success"
                            rules={[{ pattern: /^d-[a-f0-9]{32}$/, message: 'Must be a valid SendGrid template ID (d-...)' }]}
                        >
                            <Input size="large" disabled={loading || !canSaveChanges} placeholder="e.g. d-08145843143e409fbeb286a663d96632" />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            name={["emailTemplates", "LostAndFoundRequestAcknowledgment"]}
                            label="Lost Report Acknowledgment"
                            rules={[{ pattern: /^d-[a-f0-9]{32}$/, message: 'Must be a valid SendGrid template ID (d-...)' }]}
                        >
                            <Input size="large" disabled={loading || !canSaveChanges} placeholder="e.g. d-c48ddbfb3cef4a05a08df42895984dce" />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            name={["emailTemplates", "StatusChangedToReview"]}
                            label="Status Changed to Review"
                            rules={[{ pattern: /^d-[a-f0-9]{32}$/, message: 'Must be a valid SendGrid template ID (d-...)' }]}
                        >
                            <Input size="large" disabled={loading || !canSaveChanges} placeholder="e.g. d-cf6ab9374fcb4ad7b8bd4e71fb9a1675" />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            name={["emailTemplates", "StatusChangedToLFNotified"]}
                            label="Status Changed to LF Notified"
                            rules={[{ pattern: /^d-[a-f0-9]{32}$/, message: 'Must be a valid SendGrid template ID (d-...)' }]}
                        >
                            <Input size="large" disabled={loading || !canSaveChanges} placeholder="e.g. d-53c376fa52b74e4f83aadbf4753fbad9" />
                        </Form.Item>
                    </Col>
                </Row>

                <div style={{ marginBottom: 8, fontWeight: 600 }}>Localized Descriptions</div>
                <Row gutter={[12, 12]}>
                    {languageOptions.map((lang) => (
                        <Col span={12} key={lang.code}>
                            <Form.Item name={["descriptionTranslations", lang.code]} label={lang.label}>
                                <Input size="large" disabled={loading || !canSaveChanges} placeholder={`Enter ${lang.label} description`} />
                            </Form.Item>
                        </Col>
                    ))}
                </Row>
            </FormWrapper>
        </StyledModal>
    );
};

export default CreateEditModal;
